// Our main CSS
import '../css/app.css'

// interface ImportedModule {
//   name: string
//   default: object
// }
// const vueSingleFileComponents = import.meta.glob<ImportedModule>('./**/*.vue', {
//   eager: true
// })

import { createApp, ref, watch } from 'vue'
import { MenuModal } from './components'
import { FocusTrap } from 'focus-trap-vue'
import { safeMountVue } from './utils/vue-no-render'
import { initGlobalBanner } from './components/GlobalBanner'

let heroSearchModulePromise: Promise<typeof import('./components/HeroSearch/index')> | null = null
function mountHeroSearchInstances(): void {
  const targets = document.querySelectorAll<HTMLElement>('.hero-search:not([data-vue-mounted])')
  if (targets.length === 0) {
    return
  }
  if (!heroSearchModulePromise) {
    heroSearchModulePromise = import('./components/HeroSearch/index')
  }
  heroSearchModulePromise
    .then((module) => {
      targets.forEach((el) => {
        if (el.dataset.vueMounted) {
          return
        }
        el.dataset.vueMounted = '1'
        module.default(el)
      })
    })
    .catch((err) => console.error(err))
}

const app = createApp({
  setup() {
    const goBack = (): void => {
      window.history.back()
    }
    const isMenuOpen = ref(false)
    const activeMenuItem = ref(null)
    const activeMenuItemId = ref(null)
    const isMobile = ref(window.innerWidth < 768)
    const lastFocusedElement = ref<HTMLElement | null>(null)
    const isSearchOpen = ref(false)
    const searchLastFocused = ref<HTMLElement | null>(null)

    // Server-translated labels for header/menu/search/cart chrome (see
    // header.php's $headerLabels). Read once at mount from the same
    // data-labels JSON-attribute convention used by SportCalendar.vue and
    // BookOverlay.vue, so this reactive root's own re-renders (menu/search
    // toggling, isMobile changing) never race TranslatePress's client-side
    // dynamic translator back to Swedish.
    const rawLabels = document.getElementById('vue-app')?.dataset.labels
    let labels: Record<string, string> = {}
    if (rawLabels) {
      try {
        labels = JSON.parse(rawLabels)
      } catch (err) {
        console.error('Failed to parse #vue-app data-labels', err)
      }
    }
    function t(key: string): string {
      return labels[key] ?? ''
    }

    watch([isMenuOpen, isSearchOpen], ([menuOpen, searchOpen]) => {
      const anyOpen = menuOpen || searchOpen
      document.body.style.overflow = anyOpen ? 'hidden' : ''
      document.dispatchEvent(new CustomEvent('banner:menu', { detail: { open: anyOpen } }))
    })

    watch(isSearchOpen, (open) => {
      if (!open) {
        return
      }
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>(
          '#header-search-overlay input[type="search"]'
        )
        input?.focus()
      })
    })

    window.addEventListener('resize', () => {
      isMobile.value = window.innerWidth < 768
    })

    return {
      goBack,
      isMenuOpen,
      activeMenuItem,
      activeMenuItemId,
      isMobile,
      lastFocusedElement,
      isSearchOpen,
      searchLastFocused,
      t
    }
  }
})

app.component('MenuModal', MenuModal)
app.component('FocusTrap', FocusTrap)

const vueContainer = document.getElementById('vue-app')
if (vueContainer) {
  safeMountVue(app, '#vue-app', ['#klarna-checkout-container'])
}

/**
OR:

Load a js-module dynamically, if the parent element exists, in this example, a vue component. (The js-file wont load if html element with id #ExampleVueComponentWrapper does not exists)
*/
const ProductBookOverlay = document.querySelector('.product-book-overlay')
if (ProductBookOverlay) {
  import('./components/ProductBookOverlay/index')
    .then((module) => {
      module.default(ProductBookOverlay as HTMLElement)
    })
    .catch((err) => {
      console.error(err)
    })
}

const sessionTimerEl = document.querySelector('.session-timer')
if (sessionTimerEl) {
  import('./components/SessionTimer/index')
    .then((module) => {
      module.default(sessionTimerEl as HTMLElement)
    })
    .catch((err) => {
      console.error(err)
    })
}

const sportCalendarEl = document.querySelector('.sport-calendar-app')
if (sportCalendarEl) {
  import('./components/SportCalendar/index')
    .then((module) => {
      module.default(sportCalendarEl as HTMLElement)
    })
    .catch((err) => {
      console.error(err)
    })
}

mountHeroSearchInstances()

/**
 * 🦄: Now do your magic.
 */

document.addEventListener('DOMContentLoaded', () => {
  initGlobalBanner()
})

if (document.querySelector('.accordion-trigger')) {
  import('./modules/accordion').then(({ default: initAccordions }) => {
    initAccordions()
  })
}

if (document.querySelector('form.variations_form')) {
  import('./modules/single-product').then(({ default: initSingleProduct }) => {
    initSingleProduct()
  })
}

if (document.querySelector('.js-eoi-button')) {
  import('./modules/listing-eoi').then(({ default: initListingEoi }) => {
    initListingEoi()
  })
}

const initNonCritical = async () => {
  // Lazy load video handling
  const autoplaying_videos = document.querySelectorAll<HTMLVideoElement>('video[playsinline]')
  if (autoplaying_videos?.length) {
    import('./modules/inline-videos').then(({ default: handleVideos }) => {
      handleVideos(autoplaying_videos)
    })
  }

  /*
    Intersecting elements helper
*/
  const check_intersecting = document.querySelectorAll(
    '[data-check-intersecting]'
  ) as NodeListOf<HTMLElement>

  if (check_intersecting?.length) {
    import('./modules/check-intersection').then(({ default: init }) => {
      init(check_intersecting)
    })
  }
}

if ('requestIdleCallback' in window) {
  requestIdleCallback(() => initNonCritical())
} else {
  // Fallback for browsers that don't support requestIdleCallback
  setTimeout(initNonCritical, 1)
}
